import React, { Component } from "react";
import * as Icon from "react-feather";

import ResourceRow from "./ResourceRow";
import SearchableDropdown from "../common/searchable_dropdown/SearchableDropdown";
import { nativeColumnsFor } from "./subgridColumns";
import { buildOrderedColumns, reorderKeys } from "./columnOrder";

/**
 * A standalone mini-tracker for one engagement's tasks or users. Own header +
 * own horizontal scroll (columns are independent from the parent grid). Reuses
 * ResourceRow for rows.
 *
 * Props: resource, engagementId, data {fields, rows, roles, client_labels,
 * datasource_labels}, loading, isAdmin, onNativeChange, onCustomChange, onAddRow.
 */
class SubGrid extends Component {
	state = { adding: false, addName: "", addDeadline: "", addUserId: null, addUserName: "", addRoleId: null, newUser: null, busy: false, dragColKey: null, dragOverColKey: null };

	_gridRef = React.createRef();
	_MIN_COL = 70;
	_DEFAULT_CUSTOM = 220;

	// CSS variables on the sub-grid root drive each column's width (header + cells),
	// so resizing updates one variable without re-rendering rows.
	_varStyle() {
		const style = {};
		const w = this.props.columnWidths || {};
		Object.keys(w).forEach(k => {
			if (w[k]) style["--etw-" + k] = w[k] + "px";
		});
		return style;
	}

	_colWidth(colKey, fallback) {
		const w = this.props.columnWidths || {};
		return w[colKey] || fallback;
	}

	onColResizeStart = (e, colKey, currentWidth) => {
		e.preventDefault();
		e.stopPropagation();
		this._resize = { colKey, startX: e.clientX, startWidth: currentWidth, lastWidth: currentWidth };
		document.addEventListener("mousemove", this._onColResizeMove);
		document.addEventListener("mouseup", this._onColResizeEnd);
		document.body.style.cursor = "col-resize";
		document.body.style.userSelect = "none";
	};

	_onColResizeMove = e => {
		if (!this._resize) return;
		const dx = e.clientX - this._resize.startX;
		const wpx = Math.max(this._MIN_COL, Math.round(this._resize.startWidth + dx));
		this._resize.lastWidth = wpx;
		const node = this._gridRef.current;
		if (node) node.style.setProperty("--etw-" + this._resize.colKey, wpx + "px");
	};

	_onColResizeEnd = () => {
		document.removeEventListener("mousemove", this._onColResizeMove);
		document.removeEventListener("mouseup", this._onColResizeEnd);
		document.body.style.cursor = "";
		document.body.style.userSelect = "";
		const r = this._resize;
		this._resize = null;
		if (!r) return;
		if (this.props.onColResize) this.props.onColResize(r.colKey, r.lastWidth);
	};

	_resizeHandle(colKey, currentWidth) {
		return (
			<span
				className="et-grid__col-resizer"
				draggable={false}
				onMouseDown={e => this.onColResizeStart(e, colKey, currentWidth)}
				onDragStart={e => e.preventDefault()}
				onClick={e => e.stopPropagation()}
				title="Drag to resize"
			/>
		);
	}

	// ---------- Column reorder (drag & drop headers) ----------

	onColDragStart = (e, colKey) => {
		if (this._resize) {
			e.preventDefault();
			return;
		}
		this._dragKey = colKey;
		try {
			e.dataTransfer.effectAllowed = "move";
			e.dataTransfer.setData("text/plain", colKey);
		} catch (err) {
			// ignore
		}
		this.setState({ dragColKey: colKey });
	};

	onColDragOver = (e, colKey) => {
		if (!this._dragKey) return;
		e.preventDefault();
		try {
			e.dataTransfer.dropEffect = "move";
		} catch (err) {
			// ignore
		}
		if (this.state.dragOverColKey !== colKey) this.setState({ dragOverColKey: colKey });
	};

	onColDrop = (e, colKey, orderedKeys) => {
		e.preventDefault();
		const dragKey = this._dragKey;
		this._dragKey = null;
		if (!dragKey || dragKey === colKey) {
			this.setState({ dragColKey: null, dragOverColKey: null });
			return;
		}
		const next = reorderKeys(orderedKeys, dragKey, colKey);
		this.setState({ dragColKey: null, dragOverColKey: null });
		if (this.props.onColReorder) this.props.onColReorder(next);
	};

	onColDragEnd = () => {
		this._dragKey = null;
		this.setState({ dragColKey: null, dragOverColKey: null });
	};

	renderHeaderCell(d, orderedKeys) {
		const { dragColKey, dragOverColKey } = this.state;
		let label;
		let defaultW;
		if (d.kind === "native") {
			label = d.col.label;
			defaultW = d.col.width;
		} else {
			label = d.field.field_name;
			defaultW = this._DEFAULT_CUSTOM;
		}
		const basis = "var(--etw-" + d.colKey + ", " + defaultW + "px)";
		const cls =
			"et-grid__th et-grid__th--resizable et-grid__th--draggable et-grid__th--native" +
			(dragColKey === d.colKey ? " et-grid__th--dragging" : "") +
			(dragOverColKey === d.colKey && dragColKey && dragColKey !== d.colKey ? " et-grid__th--dragover" : "");
		return (
			<div
				key={d.colKey}
				className={cls}
				style={{ flex: "0 0 " + basis, width: basis }}
				title={label}
				draggable={true}
				onDragStart={e => this.onColDragStart(e, d.colKey)}
				onDragOver={e => this.onColDragOver(e, d.colKey)}
				onDrop={e => this.onColDrop(e, d.colKey, orderedKeys)}
				onDragEnd={this.onColDragEnd}
			>
				<Icon.MoreVertical size={11} className="et-grid__th-grip" />
				<span>{label}</span>
				{this._resizeHandle(d.colKey, this._colWidth(d.colKey, defaultW))}
			</div>
		);
	}

	_resetAdd() {
		this.setState({ adding: false, addName: "", addDeadline: "", addUserId: null, addUserName: "", addRoleId: null, newUser: null, busy: false });
	}

	submitTask = async () => {
		const name = (this.state.addName || "").trim();
		if (!name) return;
		this.setState({ busy: true });
		await this.props.onAddRow({ name, deadline: this.state.addDeadline || null });
		this._resetAdd();
	};

	submitMember = async () => {
		const { addUserId, addRoleId, newUser } = this.state;
		if (!addUserId && !(newUser && newUser.email)) return;
		this.setState({ busy: true });
		await this.props.onAddRow({ userId: addUserId, roleId: addRoleId, newUser });
		this._resetAdd();
	};

	renderAddRow() {
		const { resource, data } = this.props;
		const roles = (data && data.roles) || [];
		if (!this.state.adding) {
			return (
				<button className="et-subgrid__add" onClick={() => this.setState({ adding: true })}>
					<Icon.Plus size={12} />
					<span>{resource === "task" ? "Add task" : "Add member"}</span>
				</button>
			);
		}

		if (resource === "task") {
			return (
				<div className="et-subgrid__addform et-subgrid__addform--task">
					<input
						className="et-edit__col-name"
						autoFocus
						placeholder="Task name"
						value={this.state.addName}
						onChange={e => this.setState({ addName: e.target.value })}
						onKeyDown={e => e.key === "Enter" && this.submitTask()}
					/>
					<input
						type="date"
						className="et-edit__ds-select"
						style={{ maxWidth: 160 }}
						value={this.state.addDeadline}
						onChange={e => this.setState({ addDeadline: e.target.value })}
					/>
					<button className="et-btn et-btn--primary et-btn--sm" disabled={this.state.busy} onClick={this.submitTask}>
						Add
					</button>
					<button className="et-btn et-btn--ghost et-btn--sm" onClick={() => this._resetAdd()}>
						Cancel
					</button>
				</div>
			);
		}

		// member add
		const nu = this.state.newUser;
		return (
			<div className="et-subgrid__addform et-subgrid__addform--member">
				{!nu && (
					<>
						<div style={{ minWidth: 220 }}>
							<SearchableDropdown
								type="users"
								selectedName={this.state.addUserName}
								onSelect={u => this.setState({ addUserId: u ? u.id : null, addUserName: u ? u.name : "", addRoleId: u && u.defaultRole ? u.defaultRole.id : null })}
							/>
						</div>
						<select className="et-edit__ds-select" style={{ maxWidth: 160 }} value={this.state.addRoleId || ""} onChange={e => this.setState({ addRoleId: e.target.value })}>
							<option value="">Role…</option>
							{roles.map(r => (
								<option key={r.id} value={r.id}>
									{r.name}
								</option>
							))}
						</select>
						<button className="et-btn et-btn--primary et-btn--sm" disabled={this.state.busy} onClick={this.submitMember}>
							Add
						</button>
						<button className="et-btn et-btn--ghost et-btn--sm" onClick={() => this.setState({ newUser: { firstName: "", lastName: "", email: "", password: "", defaultRoleId: null } })}>
							New user
						</button>
						<button className="et-btn et-btn--ghost et-btn--sm" onClick={() => this._resetAdd()}>
							Cancel
						</button>
					</>
				)}
				{nu && (
					<>
						<input className="et-edit__col-name" placeholder="First name" value={nu.firstName} onChange={e => this.setState({ newUser: { ...nu, firstName: e.target.value } })} />
						<input className="et-edit__col-name" placeholder="Last name" value={nu.lastName} onChange={e => this.setState({ newUser: { ...nu, lastName: e.target.value } })} />
						<input className="et-edit__col-name" placeholder="Email" value={nu.email} onChange={e => this.setState({ newUser: { ...nu, email: e.target.value } })} />
						<input className="et-edit__col-name" type="password" placeholder="Password" value={nu.password} onChange={e => this.setState({ newUser: { ...nu, password: e.target.value } })} />
						<select className="et-edit__ds-select" style={{ maxWidth: 150 }} value={this.state.addRoleId || ""} onChange={e => this.setState({ addRoleId: e.target.value, newUser: { ...nu, defaultRoleId: e.target.value } })}>
							<option value="">Role…</option>
							{roles.map(r => (
								<option key={r.id} value={r.id}>
									{r.name}
								</option>
							))}
						</select>
						<button className="et-btn et-btn--primary et-btn--sm" disabled={this.state.busy} onClick={this.submitMember}>
							Create &amp; add
						</button>
						<button className="et-btn et-btn--ghost et-btn--sm" onClick={() => this.setState({ newUser: null })}>
							Back
						</button>
					</>
				)}
			</div>
		);
	}

	render() {
		const { resource, engagementId, data, loading, isAdmin, columnOrder, onNativeChange, onCustomChange } = this.props;
		const nativeColumns = nativeColumnsFor(resource);
		const fields = (data && data.fields) || [];
		const rows = (data && data.rows) || [];
		const fieldsById = {};
		fields.forEach(f => (fieldsById[f.id] = f));

		const hidden = this.props.hiddenColumns || [];
		const anchorCol = nativeColumns.find(c => c.anchor);
		const nonAnchorNative = nativeColumns.filter(c => !c.anchor);
		const orderedHeaders = buildOrderedColumns(nonAnchorNative, fields, columnOrder).filter(
			d => hidden.indexOf(d.colKey) === -1,
		);
		const orderedKeys = orderedHeaders.map(d => d.colKey);

		return (
			<div className="et-subgrid">
				<div className="et-subgrid__title">
					{resource === "task" ? <Icon.CheckSquare size={12} /> : <Icon.Users size={12} />}
					<span>{resource === "task" ? "Tasks" : "Users"}</span>
					<span className="et-subgrid__count">{rows.length}</span>
				</div>

				<div className="et-grid et-grid--sub" ref={this._gridRef} style={this._varStyle()}>
					<div className="et-grid__head">
						{anchorCol && (
							<div
								className="et-grid__th et-grid__th--resizable et-grid__th--anchor et-grid__th--sub-anchor"
								key={anchorCol.key}
								style={{
									flex: "0 0 var(--etw-n_" + anchorCol.key + ", " + anchorCol.width + "px)",
									width: "var(--etw-n_" + anchorCol.key + ", " + anchorCol.width + "px)",
								}}
							>
								<span>{anchorCol.label}</span>
								{this._resizeHandle("n_" + anchorCol.key, this._colWidth("n_" + anchorCol.key, anchorCol.width))}
							</div>
						)}
						{orderedHeaders.map(d => this.renderHeaderCell(d, orderedKeys))}
					</div>

					<div className="et-grid__body">
						{loading && <div className="et-subgrid__loading">Loading…</div>}
						{!loading && rows.length === 0 && (
							<div className="et-subgrid__empty">{resource === "task" ? "No tasks yet." : "No members yet."}</div>
						)}
						{!loading &&
							rows.map(row => (
								<ResourceRow
									key={row.record_id}
									resource={resource}
									engagementId={engagementId}
									row={row}
									nativeColumns={nativeColumns}
									fields={fields}
									columnOrder={columnOrder}
									hiddenColumns={hidden}
									fieldsById={fieldsById}
									datasourceLabels={data && data.datasource_labels}
									roles={(data && data.roles) || []}
									clientLabels={(data && data.client_labels) || {}}
									isAdmin={isAdmin}
									onNativeChange={onNativeChange}
									onCustomChange={onCustomChange}
								/>
							))}
					</div>
				</div>

				{this.renderAddRow()}
			</div>
		);
	}
}

export default SubGrid;
